import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash, FloppyDisk, Timer, Sparkle, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const READ_WPS = 2.5;
const readSeconds = (text) => {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return words / READ_WPS;
};

const newPoint = (text = "") => ({ id: crypto.randomUUID(), text });

export default function ScriptEditor() {
  useSeo("Script Editor", "Write video scripts with a live hook read-time timer that keeps your opening under 5 seconds.");
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isNew = id === "new";
  const [title, setTitle] = useState(() => (id === "new" ? params.get("title") || "" : ""));
  const [hook, setHook] = useState(() => (id === "new" ? params.get("hook") || "" : ""));
  const [points, setPoints] = useState([newPoint()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get("/scripts").then((res) => {
      const s = res.data.find((x) => x.id === id);
      if (!s) return navigate("/scripts/new");
      setTitle(s.title);
      setHook(s.hook);
      setPoints(s.mainPoints?.length ? s.mainPoints.map((t) => newPoint(t)) : [newPoint()]);
    });
  }, [id, isNew, navigate]);

  const secs = readSeconds(hook);
  const under5 = secs <= 5;

  const save = async () => {
    setSaving(true);
    const body = { title, hook, hookReadSeconds: Math.round(secs * 10) / 10, mainPoints: points.map((p) => p.text.trim()).filter(Boolean), status: "Draft" };
    try {
      if (isNew) {
        const res = await api.post("/scripts", body);
        toast.success("Script saved");
        navigate(`/scripts/${res.data.id}`, { replace: true });
      } else {
        await api.put(`/scripts/${id}`, body);
        toast.success("Script updated");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const setPoint = (id, v) => setPoints(points.map((p) => (p.id === id ? { ...p, text: v } : p)));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-10 lg:py-14" data-testid="script-editor-page">
      <div className="fade-up flex items-center justify-between mb-8">
        <button data-testid="script-back-btn" onClick={() => navigate("/calendar")} className="inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--text)] transition-colors">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              data-testid="teleprompter-btn"
              onClick={() => navigate(`/teleprompter/${id}`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full surface text-sm font-semibold hover:border-[var(--glow)] transition-colors"
            >
              <Play size={14} weight="fill" /> Teleprompter
            </button>
          )}
          <button
            data-testid="script-save-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[var(--glow)] text-white text-sm font-semibold hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
          >
            <FloppyDisk size={15} weight="bold" /> {saving ? "Saving..." : "Save script"}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="fade-up">
          <label className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-semibold block mb-2">Title</label>
          <input
            data-testid="script-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Your next banger title..."
            className="w-full bg-transparent border-b-2 border-[var(--border-c)] focus:border-[var(--glow)] outline-none py-2 text-2xl sm:text-3xl font-display font-black tracking-tight placeholder:text-[var(--border-c)]"
          />
        </div>

        <div className="surface rounded-[20px] p-6 fade-up-1">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Hook</label>
            <span
              data-testid="hook-timer-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
              style={{
                background: under5 ? "rgba(53,199,89,0.15)" : "rgba(154,154,165,0.15)",
                color: under5 ? "var(--success)" : "var(--muted)",
              }}
            >
              <Timer size={13} weight="bold" /> ~{secs.toFixed(1)}s read
            </span>
          </div>
          <textarea
            data-testid="script-hook-input"
            value={hook}
            onChange={(e) => setHook(e.target.value)}
            rows={4}
            placeholder="The first 5 seconds decide everything. Write your opening line..."
            className="w-full bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)] resize-y"
          />
          <div className="flex items-center justify-between gap-3 mt-2">
            <p className="text-xs text-[var(--muted)]">
              {under5 ? "✓ Punchy. Viewers will stick around." : "Aim under 5 seconds — trim it down to keep retention high."}
            </p>
            <button
              data-testid="script-ai-titles-btn"
              onClick={() => navigate(`/tools/ai-titles?hook=${encodeURIComponent(hook)}`)}
              disabled={!hook.trim()}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--glow)] hover:underline disabled:opacity-40 shrink-0"
            >
              <Sparkle size={13} weight="bold" /> AI title ideas
            </button>
          </div>
        </div>

        <div className="surface rounded-[20px] p-6 fade-up-2">
          <label className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-semibold block mb-4">Main points</label>
          <div className="space-y-3">
            {points.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3">
                <span className="w-7 h-7 shrink-0 rounded-full bg-[var(--glow)]/15 text-[var(--glow)] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <input
                  data-testid={`script-point-input-${i}`}
                  value={p.text}
                  onChange={(e) => setPoint(p.id, e.target.value)}
                  placeholder={`Point ${i + 1}`}
                  className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-2.5 text-sm outline-none focus:border-[var(--glow)]"
                />
                {points.length > 1 && (
                  <button data-testid={`script-point-delete-${i}`} onClick={() => setPoints(points.filter((x) => x.id !== p.id))} className="text-[var(--muted)] hover:text-[var(--coral)] transition-colors">
                    <Trash size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            data-testid="script-add-point-btn"
            onClick={() => setPoints([...points, newPoint()])}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--glow)] hover:underline"
          >
            <Plus size={14} weight="bold" /> Add point
          </button>
        </div>
      </div>
    </div>
  );
}
